import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getLocationInfoErrors} from 'molbio/bulk-assembly/assemble-modal/FinalizationUtil';
import {PickLocationStep} from 'molbio/bulk-assembly/assemble-modal/PickLocationStep';
import {FinalizationLocationInfo} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {DEFAULT_SAVE_INFORMATION, LocationInformation} from 'molbio/components/SaveLocationPicker';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';

export enum PerformAssemblyModalStep {
  SAVE_FRAGMENTS = 'SAVE_FRAGMENTS',
  SAVE_CONSTRUCTS = 'SAVE_CONSTRUCTS',
  SAVE_PRIMERS = 'SAVE_PRIMERS',
}

const STEP_KEY_TO_DISPLAY_STRING: {[key in PerformAssemblyModalStep]: string} = {
  [PerformAssemblyModalStep.SAVE_FRAGMENTS]: 'amplified fragments',
  [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: 'constructs',
  [PerformAssemblyModalStep.SAVE_PRIMERS]: 'primers',
};

/**
 * Gets a list of errors for a specified step of the modal.
 * @param stepKey The step to verify.
 * @param locationInfo Information about where to save the entities for the current step and whether to add
 *   them to a worklist.
 * @param saveEntities Whether to save entities for the current step at all.
 */
function getErrorsForStep({
  stepKey,
  locationInfo,
  saveEntities,
}: {
  stepKey: PerformAssemblyModalStep;
  locationInfo: LocationInformation;
  saveEntities: boolean;
}): Array<string> {
  let errors: Array<string>;
  if (saveEntities) {
    const displayString = STEP_KEY_TO_DISPLAY_STRING[stepKey];
    errors = getLocationInfoErrors({locationInfo, displayString});
  } else {
    errors = [];
  }

  return errors;
}

/**
 * Get the ModalButtons to display in the modal footer.
 * @param stepKey The current step.
 * @param setStep A function to set the current step.
 * @param statusByStep The status for each step.
 * @param setStatusByStep A function to set the status for each step.
 * @param locationInfo Information about where to save the entities for the current step and whether to add
 *   them to a worklist.
 * @param saveEntities Whether to save entities for the current step at all.
 * @param onClickAssemble A function to be called when the modal is submitted.
 * @param canCreatePrimers Whether the assembly may create primers
 */
function getStepButtons({
  stepKey,
  setStepKey,
  statusByStep,
  setStatusByStep,
  locationInfo,
  saveEntities,
  onClickAssemble,
  canCreatePrimers,
}: {
  stepKey: PerformAssemblyModalStep;
  setStepKey: (step: PerformAssemblyModalStep) => void;
  statusByStep: {
    [key in PerformAssemblyModalStep]: StepItemStatus | null;
  };
  setStatusByStep: (statusByStep: {[key in PerformAssemblyModalStep]: StepItemStatus | null}) => void;
  locationInfo: LocationInformation;
  saveEntities: boolean;
  onClickAssemble: () => void;
  canCreatePrimers: boolean;
}): Array<ModalButton> {
  const errors = getErrorsForStep({
    stepKey,
    locationInfo,
    saveEntities,
  });
  const disableNextButton = errors.length > 0;
  const assembleButton = {
    text: 'Assemble',
    submit: true,
    disabled: disableNextButton,
    ...(disableNextButton ? {tooltipTitle: errors.join(' ')} : {}),
    onClick: onClickAssemble,
  };
  if (stepKey === PerformAssemblyModalStep.SAVE_FRAGMENTS) {
    return [
      {
        text: 'Back',
        onClick: () => {
          setStepKey(PerformAssemblyModalStep.SAVE_CONSTRUCTS);
          setStatusByStep({
            ...statusByStep,
            [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: StepItemStatus.UNVISITED,
          });
        },
      },
      canCreatePrimers
        ? {
            text: 'Next',
            submit: true,
            disabled: disableNextButton,
            tooltipTitle: disableNextButton ? errors.join(' ') : undefined,
            onClick: () => {
              setStepKey(PerformAssemblyModalStep.SAVE_PRIMERS);
              if (statusByStep.SAVE_FRAGMENTS !== StepItemStatus.ERROR) {
                setStatusByStep({
                  ...statusByStep,
                  [PerformAssemblyModalStep.SAVE_FRAGMENTS]: StepItemStatus.DONE,
                });
              }
            },
            dataTestAttrs: {component: 'PerformAssemblyModal', key: ModalButtonKey.NEXT},
          }
        : assembleButton,
    ];
  } else if (stepKey === PerformAssemblyModalStep.SAVE_CONSTRUCTS) {
    return [
      {text: 'Cancel', cancel: true},
      {
        text: 'Next',
        submit: true,
        disabled: disableNextButton,
        tooltipTitle: disableNextButton ? errors.join(' ') : undefined,
        onClick: () => {
          if (statusByStep.SAVE_CONSTRUCTS !== StepItemStatus.ERROR) {
            setStatusByStep({
              ...statusByStep,
              [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: StepItemStatus.DONE,
            });
          }
          setStepKey(PerformAssemblyModalStep.SAVE_FRAGMENTS);
        },
        dataTestAttrs: {component: 'PerformAssemblyModal', key: ModalButtonKey.NEXT},
      },
    ];
  } else {
    return [
      {
        text: 'Back',
        onClick: () => {
          setStepKey(PerformAssemblyModalStep.SAVE_FRAGMENTS);
          setStatusByStep({
            ...statusByStep,
            [PerformAssemblyModalStep.SAVE_PRIMERS]: StepItemStatus.UNVISITED,
          });
        },
      },
      assembleButton,
    ];
  }
}

export interface PerformAssemblyModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (saveInformation: FinalizationLocationInfo) => void;
  currentUserId: string;
  canCreatePrimers: boolean;
  numConstructs: number;
}

/**
 * A modal used to select the location(s) in which the assembly outputs should be saved. Upon submission, the
 * assembly is completed on the backend.
 *
 * @param {PerformAssemblyModalProps} props Component props
 * @param {Function} props.onRequestClose Handler for closing the modal without submitting
 * @param {Function} props.onRequestSubmit Handler for submitting the modal
 * @param {string} props.currentUserId The API identifier of the current user, which is used for displaying
 *                                     the create worklist UI
 * @param {boolean} props.canCreatePrimers Whether or not the assembly may generate primers
 */
export default function PerformAssemblyModal(props: PerformAssemblyModalProps): JSX.Element {
  const initialSaveInformation = {
    ...DEFAULT_SAVE_INFORMATION,
    targetFolderId: props.initialFolderId ?? undefined,
    tagSchemaId: null,
  };
  const [saveFragments, setSaveFragments] = useState<boolean>(false);
  const [fragmentLocationInfo, setFragmentLocationInfo] =
    useState<LocationInformation>(initialSaveInformation);
  const [constructLocationInfo, setConstructLocationInfo] =
    useState<LocationInformation>(initialSaveInformation);
  const [savePrimers, setSavePrimers] = useState<boolean>(true);
  const [primerLocationInfo, setPrimerLocationInfo] = useState<LocationInformation>(initialSaveInformation);
  const [statusByStep, setStatusByStep] = useState<{
    [key in PerformAssemblyModalStep]: StepItemStatus | null;
  }>({
    [PerformAssemblyModalStep.SAVE_FRAGMENTS]: StepItemStatus.UNVISITED,
    [PerformAssemblyModalStep.SAVE_CONSTRUCTS]: StepItemStatus.UNVISITED,
    [PerformAssemblyModalStep.SAVE_PRIMERS]: props.canCreatePrimers ? StepItemStatus.UNVISITED : null,
  });
  const initialStep = PerformAssemblyModalStep.SAVE_CONSTRUCTS;
  const [stepKey, setStepKey] = useState<PerformAssemblyModalStep>(initialStep);

  let locationInfo: LocationInformation;
  let saveEntities: boolean;
  switch (stepKey) {
    case PerformAssemblyModalStep.SAVE_FRAGMENTS:
      locationInfo = fragmentLocationInfo;
      saveEntities = saveFragments;
      break;
    case PerformAssemblyModalStep.SAVE_CONSTRUCTS:
      locationInfo = constructLocationInfo;
      saveEntities = true;
      break;
    case PerformAssemblyModalStep.SAVE_PRIMERS:
    default:
      locationInfo = primerLocationInfo;
      saveEntities = savePrimers;
      break;
  }

  useEffect(() => {
    const currentStatus = statusByStep[stepKey];
    const errors = getErrorsForStep({
      stepKey,
      locationInfo,
      saveEntities,
    });
    const newStatus = errors.length > 0 ? StepItemStatus.ERROR : StepItemStatus.UNVISITED;
    if (currentStatus !== newStatus) {
      setStatusByStep({
        ...statusByStep,
        [stepKey]: newStatus,
      });
    }
  }, [stepKey, statusByStep, setStatusByStep, locationInfo, saveEntities]);

  const onClickAssemble = (): void => {
    assert(
      (fragmentLocationInfo.targetFolderId != null || !saveFragments) &&
        constructLocationInfo.targetFolderId != null &&
        (primerLocationInfo.targetFolderId != null || !savePrimers),
      'Expected fragment, construct, and primer folders to be specified when saving those entities'
    );
    props.onRequestSubmit({
      fragmentSaveInfo: saveFragments
        ? {
            folderId: fragmentLocationInfo.targetFolderId!,
            tagSchemaId: fragmentLocationInfo.tagSchemaId,
            worklistId: fragmentLocationInfo.worklistParams?.targetWorklistId ?? null,
            newWorklistName: fragmentLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
          }
        : null,
      constructSaveInfo: {
        folderId: constructLocationInfo.targetFolderId,
        tagSchemaId: constructLocationInfo.tagSchemaId,
        worklistId: constructLocationInfo.worklistParams?.targetWorklistId ?? null,
        newWorklistName: constructLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
      },
      primerSaveInfo: savePrimers
        ? {
            folderId: primerLocationInfo.targetFolderId!,
            tagSchemaId: primerLocationInfo.tagSchemaId,
            worklistId: primerLocationInfo.worklistParams?.targetWorklistId ?? null,
            newWorklistName: primerLocationInfo.worklistParams?.newWorklistInfo?.name ?? null,
          }
        : null,
    });
  };

  const buttons = getStepButtons({
    stepKey,
    setStepKey,
    statusByStep,
    setStatusByStep,
    locationInfo,
    saveEntities,
    onClickAssemble,
    canCreatePrimers: props.canCreatePrimers,
  });

  const sharedWorklistParams = {
    newWorklistOwnerId: props.currentUserId,
    shouldShowOverflowWarningForNewWorklist:
      props.numConstructs > BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST,
    shouldShowOverflowWarningForExistingWorklist: props.numConstructs > 100,
  };

  return (
    <Modal title="Assemble DNA" buttons={buttons} onRequestClose={props.onRequestClose}>
      <StepNav<PerformAssemblyModalStep>
        steps={[
          {key: PerformAssemblyModalStep.SAVE_CONSTRUCTS, title: 'Save constructs'},
          {
            key: PerformAssemblyModalStep.SAVE_FRAGMENTS,
            title: 'Save fragments',
          },
          ...(props.canCreatePrimers
            ? [{key: PerformAssemblyModalStep.SAVE_PRIMERS, title: 'Save primers'}]
            : []),
        ]}
        statusByStep={statusByStep}
        currentStepKey={stepKey}
        onUpdateCurrentStep={setStepKey}
        variant={StepNavVariant.FLAT}
      />
      <>
        {stepKey === PerformAssemblyModalStep.SAVE_FRAGMENTS && (
          <PickLocationStep
            locationInfo={fragmentLocationInfo}
            onUpdateLocationInfo={setFragmentLocationInfo}
            worklistParams={{
              ...sharedWorklistParams,
              useWorklistCheckboxLabel: 'Add amplified fragments to a worklist',
              pluralEntityLabel: 'fragments',
            }}
            isRequiredParams={{
              isRequired: false,
              checkboxLabel: 'Create DNA Sequences to represent amplified fragments',
              checkboxTooltip:
                'If you deselect this option, Benchling will not create any new amplified fragments as part of this assembly',
              saveEntities: saveFragments,
              onUpdateSaveEntities: setSaveFragments,
              bannerText: 'Saving fragments is optional.',
            }}
            oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
          />
        )}
        {stepKey === PerformAssemblyModalStep.SAVE_CONSTRUCTS && (
          <PickLocationStep
            locationInfo={constructLocationInfo}
            onUpdateLocationInfo={setConstructLocationInfo}
            worklistParams={{
              ...sharedWorklistParams,
              useWorklistCheckboxLabel: 'Add constructs to a worklist',
              pluralEntityLabel: 'constructs',
            }}
            stepDescription="Add constructs to a folder and optionally set a schema"
            isRequiredParams={{isRequired: true}}
            oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
          />
        )}
        {stepKey === PerformAssemblyModalStep.SAVE_PRIMERS && (
          <PickLocationStep
            locationInfo={primerLocationInfo}
            onUpdateLocationInfo={setPrimerLocationInfo}
            worklistParams={{
              ...sharedWorklistParams,
              useWorklistCheckboxLabel: 'Add primers to a worklist',
              pluralEntityLabel: 'primers',
            }}
            isRequiredParams={{
              isRequired: false,
              checkboxLabel: 'Create DNA Oligos to represent newly designed primers',
              checkboxTooltip:
                'If you deselect this option, Benchling will not create any new primers as part of this assembly',
              saveEntities: savePrimers,
              onUpdateSaveEntities: setSavePrimers,
              bannerText: 'Saving primers is optional.',
            }}
            oligoAwareFolderItemTypes={[ModelTypeConstants.DNA_OLIGO]}
          />
        )}
      </>
    </Modal>
  );
}
